// lib/whatsapp/cta-context.ts
/**
 * Resolutor de comportamiento de los CTAs WhatsApp globales (header/footer) —
 * context-aware 2026-06-11.
 *
 * Problema original (founder): el botón WSP del header abría SIEMPRE el mismo
 * asistente de fechas de apartamentos, sin importar dónde estuviera el cliente:
 *  - En /reservas con búsqueda activa volvía a pedir fechas (cuando el CTA
 *    "Enviar búsqueda por WhatsApp" de la misma página ya las manda).
 *  - En /tours pedía check-in/check-out, incoherente con un tour de un día.
 *  - En fichas de apto/edificio ignoraba el contexto y pisaba el `source`.
 *
 * Este módulo es PURO (sin DOM): recibe pathname + query string + el beacon de
 * página (dataset del body que montan las fichas) y devuelve qué debe hacer el
 * CTA. Mismo principio que el deep-link `?intent=wsp` de /reservas: si la URL
 * ya trae fechas, el asistente sobra.
 */

import type { WhatsAppIntakeContext } from '@/components/whatsapp/WhatsAppIntakeSheet';
import type { WspEntryContext } from '@/lib/analytics/wsp-qualification';
import {
  buildSearchMessage,
  buildApartmentMessage,
  buildTourMessage,
  type SearchPriceRange,
} from '@/lib/whatsapp/qualified-message';
import { isValidISODate } from '@/lib/dateUtils';
import { getMinTourContactDateISO } from '@/lib/tours/booking-lead-time';

/** Datos de ficha publicados por WspPageBeacon (apartamento/edificio/tour). */
export interface WspPageBeaconData {
  apartmentId?: string;
  apartmentName?: string;
  buildingSlug?: string;
  buildingName?: string;
  neighborhood?: string;
  tourSlug?: string;
  tourName?: string;
  tourMinDateISO?: string;
  /** Datos públicos para recalcular el cutoff exactamente al hacer clic. */
  tourStartTime?: string;
  tourBookingLeadDays?: string;
  /** Vertical de una guía del blog: 'stay' solo si lleva a alojamiento. */
  pageVertical?: 'tour' | 'stay';
}

/** Beacon completo leído del dataset del body: datos de ficha + el resumen de
 *  precios que AvailabilitySearch publica con la búsqueda actual. */
export interface WspBeaconData extends WspPageBeaconData {
  /** searchArrival/searchDeparture amarran el rango a SUS fechas: el resolutor
   *  solo lo usa si coinciden con la URL (nunca cita un precio de otras fechas). */
  searchArrival?: string;
  searchDeparture?: string;
  searchCount?: string;
  searchMin?: string;
  searchMax?: string;
  searchNights?: string;
}

export interface WspCtaPage {
  pathname: string;
  /** window.location.search (acepta con o sin '?'). */
  search: string;
  beacon: WspBeaconData;
  placement: 'header' | 'footer';
}

export type WspCtaBehavior =
  | {
      /** Búsqueda activa en la URL: directo a WhatsApp con mensaje calificado,
       *  sin volver a preguntar fechas. */
      kind: 'bypass';
      vertical: 'stay' | 'tour';
      message: string;
      ctaPosition: string;
      entryContext: WspEntryContext;
      source: string;
      arrival: string;
      departure: string;
      numAdults?: number;
      guestsTotal?: number;
      apartmentId?: string;
      tourSlug?: string;
    }
  | { kind: 'intake'; context: WhatsAppIntakeContext };

/** Misma etiqueta que usa AvailabilitySearch para el filtro de edificio. */
const BUILDING_LABELS: Record<string, string> = {
  'nuevo-conquistador': 'Edificio Nuevo Conquistador',
};

function parseRange(arrival: string | null, departure: string | null): { arrival: string; departure: string } | null {
  if (!arrival || !departure) return null;
  if (!isValidISODate(arrival) || !isValidISODate(departure)) return null;
  if (departure <= arrival) return null;
  return { arrival, departure };
}

function parsePositiveInteger(
  raw: string | null,
  max = 100
): number | undefined {
  if (!raw || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 && value <= max
    ? value
    : undefined;
}

function parseChildrenAges(raw: string | null): Array<'4-' | '5+'> {
  if (!raw) return [];
  return raw
    .split(',')
    .filter((age): age is '4-' | '5+' => age === '4-' || age === '5+')
    .slice(0, 10);
}

/** Rango de precios del beacon, SOLO si fue publicado para estas mismas fechas
 *  (evita citar un precio de una búsqueda anterior si la URL cambió a mano). */
function priceRangeFor(
  beacon: WspBeaconData,
  arrival: string,
  departure: string
): SearchPriceRange | undefined {
  if (beacon.searchArrival !== arrival || beacon.searchDeparture !== departure) return undefined;
  const count = parsePositiveInteger(beacon.searchCount ?? null, 1000);
  const nights = parsePositiveInteger(beacon.searchNights ?? null, 365);
  if (!count) return undefined;
  if (!beacon.searchMin || !beacon.searchMax) return undefined;
  return {
    count,
    minFormatted: beacon.searchMin,
    maxFormatted: beacon.searchMax,
    nights: nights ?? 1,
  };
}

export function resolveWspCtaBehavior(page: WspCtaPage): WspCtaBehavior {
  const { pathname, beacon, placement } = page;
  const params = new URLSearchParams(page.search);
  // Atribución: si la URL ya trae `source` (landing Ads, home, intake previo)
  // se preserva — el CTA global no debe pisar el funnel original.
  const source = params.get('source') ?? placement;
  const ctaPosition = `intake_${placement}`;

  // --- Tours: fecha única, nunca check-in/check-out ---
  if (pathname === '/tours' || pathname.startsWith('/tours/')) {
    const tourDate = params.get('fecha');
    const people = parsePositiveInteger(params.get('personas'), 30);
    const parsedLeadDays = /^\d+$/.test(beacon.tourBookingLeadDays ?? '')
      ? Number(beacon.tourBookingLeadDays)
      : null;
    const minTourDate =
      beacon.tourStartTime && parsedLeadDays !== null && parsedLeadDays <= 30
        ? getMinTourContactDateISO(beacon.tourStartTime, parsedLeadDays)
        : isValidISODate(beacon.tourMinDateISO)
          ? beacon.tourMinDateISO
          : null;
    if (
      tourDate &&
      isValidISODate(tourDate) &&
      (!minTourDate || tourDate >= minTourDate) &&
      people
    ) {
      return {
        kind: 'bypass',
        vertical: 'tour',
        message: buildTourMessage({
          date: tourDate,
          people,
          tourName: beacon.tourName,
        }),
        ctaPosition: `${placement}_qualified_bypass`,
        entryContext: beacon.tourSlug ? 'tour_detail' : 'tours_hub',
        source,
        arrival: tourDate,
        departure: tourDate,
        numAdults: people,
        guestsTotal: people,
        tourSlug: beacon.tourSlug,
      };
    }
    return {
      kind: 'intake',
      context: {
        ctaPosition,
        entryContext: beacon.tourSlug ? 'tour_detail' : 'tours_hub',
        variant: 'tour',
        tourSlug: beacon.tourSlug,
        tourName: beacon.tourName,
        tourMinDateISO: minTourDate ?? undefined,
        tourStartTime: beacon.tourStartTime,
        tourBookingLeadDays: parsedLeadDays ?? undefined,
        source,
      },
    };
  }

  // --- /reservas con búsqueda activa: equivalente de "Enviar búsqueda por
  //     WhatsApp" — no se vuelve a preguntar nada ---
  if (pathname === '/reservas') {
    const range = parseRange(params.get('arrival'), params.get('departure'));
    if (range) {
      const numAdults = parsePositiveInteger(params.get('numAdults'));
      const guestAdults =
        parsePositiveInteger(params.get('guestAdults')) ?? numAdults;
      const childrenAges = parseChildrenAges(params.get('childrenAges'));
      const guestsTotal = guestAdults
        ? guestAdults + childrenAges.length
        : numAdults;
      const buildingName = BUILDING_LABELS[params.get('building') ?? ''];
      const neighborhood = params.get('neighborhood') ?? undefined;
      return {
        kind: 'bypass',
        vertical: 'stay',
        message: buildSearchMessage({
          ...range,
          guests: guestsTotal ?? 0,
          numAdults: guestAdults,
          numChildren: childrenAges.length || undefined,
          buildingName,
          neighborhood,
          priceRange: priceRangeFor(beacon, range.arrival, range.departure),
        }),
        ctaPosition: `${placement}_qualified_bypass`,
        entryContext: 'reservas',
        source,
        ...range,
        numAdults,
        guestsTotal,
      };
    }
    return {
      kind: 'intake',
      context: { ctaPosition, entryContext: 'reservas', source, mode: 'message' },
    };
  }

  // --- Ficha de apartamento ---
  if (/^\/apartamentos\/[^/]+$/.test(pathname)) {
    // La ficha usa checkIn/checkOut/guests; los links desde /reservas pueden
    // traer arrival/departure/numAdults. Se aceptan ambas convenciones.
    const range =
      parseRange(params.get('checkIn'), params.get('checkOut')) ??
      parseRange(params.get('arrival'), params.get('departure'));
    const guests =
      parsePositiveInteger(params.get('guests')) ??
      parsePositiveInteger(params.get('numAdults'));

    if (range && beacon.apartmentName) {
      return {
        kind: 'bypass',
        vertical: 'stay',
        message: buildApartmentMessage({
          ...range,
          guests: guests ?? 0,
          apartmentName: beacon.apartmentName,
        }),
        ctaPosition: `${placement}_qualified_bypass`,
        entryContext: 'apartment_detail',
        source,
        ...range,
        numAdults: guests,
        guestsTotal: guests,
        apartmentId: beacon.apartmentId,
      };
    }
    return {
      kind: 'intake',
      context: {
        ctaPosition,
        entryContext: 'apartment_detail',
        apartmentId: beacon.apartmentId,
        apartmentName: beacon.apartmentName,
        source,
        // Con la ficha identificada el mensaje nombra el apto (mode message,
        // igual que BookingSidebar); sin beacon cae al flujo results estándar.
        mode: beacon.apartmentName ? 'message' : 'results',
      },
    };
  }

  // --- Landing de edificio: heredar edificio/barrio (antes salía genérico) ---
  if (/^\/edificios\/[^/]+$/.test(pathname)) {
    const slugFromPath = pathname.split('/')[2];
    return {
      kind: 'intake',
      context: {
        ctaPosition,
        entryContext: 'building',
        buildingSlug: beacon.buildingSlug ?? slugFromPath,
        buildingName: beacon.buildingName ?? BUILDING_LABELS[slugFromPath],
        neighborhood: beacon.neighborhood,
        source,
        mode: 'message',
      },
    };
  }

  // --- Guías del blog: casi todas son de islas, playas y pasadías. Antes
  //     caían al intake de apartamentos y el cliente llegaba a WhatsApp
  //     cotizando una estadía; solo las guías de alojamiento conservan stay ---
  if (pathname.startsWith('/blog/') && beacon.pageVertical !== 'stay') {
    return {
      kind: 'intake',
      context: { ctaPosition, entryContext: 'blog', variant: 'tour', source },
    };
  }

  // --- Resto del sitio: intake estándar de apartamentos ---
  return {
    kind: 'intake',
    context: { ctaPosition, entryContext: placement, source, mode: 'message' },
  };
}

/** Lee el beacon publicado por WspPageBeacon (fichas) y AvailabilitySearch
 *  (rango de precios) desde el dataset del body. */
export function readWspBeacon(dataset: DOMStringMap): WspBeaconData {
  return {
    apartmentId: dataset.wspApartmentId,
    apartmentName: dataset.wspApartmentName,
    buildingSlug: dataset.wspBuildingSlug,
    buildingName: dataset.wspBuildingName,
    neighborhood: dataset.wspNeighborhood,
    tourSlug: dataset.wspTourSlug,
    tourName: dataset.wspTourName,
    tourMinDateISO: dataset.wspTourMinDate,
    tourStartTime: dataset.wspTourStartTime,
    tourBookingLeadDays: dataset.wspTourBookingLeadDays,
    pageVertical:
      dataset.wspPageVertical === 'tour' || dataset.wspPageVertical === 'stay'
        ? dataset.wspPageVertical
        : undefined,
    searchArrival: dataset.wspSearchArrival,
    searchDeparture: dataset.wspSearchDeparture,
    searchCount: dataset.wspSearchCount,
    searchMin: dataset.wspSearchMin,
    searchMax: dataset.wspSearchMax,
    searchNights: dataset.wspSearchNights,
  };
}
